import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';

import { Skeleton } from '@/components';
import {
  selectReportDateRange,
  useAppSelector,
  useFetchTopListenersReportQuery,
} from '@/store';

import { styles } from '../../styles';
import { EmptyChartView, EmptyViewImageKeys } from '../EmptyChartView';
import { TileHeader } from '../TileHeader';
import { Avatar } from './Avatar';

export const TopListeners = () => {
  const dateRange = useAppSelector(selectReportDateRange);

  const { data = [], isFetching } = useFetchTopListenersReportQuery(
    {
      dateRange,
    },
    {
      skip: !dateRange.fromDate || !dateRange.toDate,
    },
  );

  const isEmpty = useMemo(() => {
    if (!data) return false;
    return data.length === 0;
  }, [data]);

  return (
    <Box sx={[styles.tile, styles.tileWithMinHeight, styles.column]}>
      <TileHeader
        title="Top Listeners"
        description="Highlights team members with the  most listened to episodes in the selected time period. Compliance is not included in this metric. Each episode is counted once per team member."
      />
      {isFetching && (
        <Box>
          {Array(5)
            .fill(null)
            .map((_, idx: number) => (
              <Box key={idx} sx={styles.listItem}>
                <Skeleton />
              </Box>
            ))}
        </Box>
      )}
      {!isFetching && isEmpty && (
        <EmptyChartView
          title={EMPTY_CHART_VIEW_TITLE}
          subtitle={EMPTY_CHART_VIEW_SUBTITLE}
          imageKey={EmptyViewImageKeys.headphones}
        />
      )}
      {!isFetching && !isEmpty && data && (
        <>
          <Box sx={{ flexGrow: 1 }}>
            {data.slice(0, 10).map((member, idx) => (
              <Box key={member.userId} sx={styles.listItem}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Box sx={{}}>
                    <Typography variant="body2SemiBold" sx={{ width: 24 }}>
                      {idx + 1}.
                    </Typography>
                  </Box>
                  <Avatar name={member.name} />
                  <Box>
                    <Typography variant="body3SemiBold">
                      {member.name}
                    </Typography>
                    <Typography variant="caption1" sx={{ color: 'text.light' }}>
                      {member.email}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography
                    variant="body2SemiBold"
                    sx={{ color: 'secondary.main' }}>
                    {member.episodesCompleted}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

const EMPTY_CHART_VIEW_TITLE =
  'This leaderboard updates with listening activity';
const EMPTY_CHART_VIEW_SUBTITLE =
  'Encourage your team to listen and their names show up here.';
